import React, { useCallback, useEffect, useRef, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { useAuth } from "../auth/AuthProvider";
import { normalizeClaims } from "../auth/landing";
import MonthlyBillPreview from "../billing/MonthlyBillPreview";
import BillingAccount from "../billing/BillingAccount";
import NeoSiteUsage from "../billing/NeoSiteUsage";
import { currentBillingMonth, getMonthlyBillPreview, previewErrorMessage } from "../billing/billingPreviewService";
import "./neoBillingMonthly.css";

const money = (cents) => cents == null ? "Needs review" : new Intl.NumberFormat("en-ZA", {
  style: "currency", currency: "ZAR", maximumFractionDigits: 2,
}).format(cents / 100);
const monthLabel = (month) => new Intl.DateTimeFormat("en-ZA", {
  timeZone: "Africa/Johannesburg", month: "long", year: "numeric",
}).format(new Date(`${month}-01T12:00:00+02:00`));
const dateLabel = (value) => value ? new Intl.DateTimeFormat("en-ZA", {
  timeZone: "Africa/Johannesburg", dateStyle: "medium",
}).format(new Date(value.length === 10 ? `${value}T12:00:00+02:00` : value)) : "—";
const statusLabel = (value) => value ? String(value).replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()) : "Unavailable";

// Only a server ledger summary for this company can provide balances or credit.
function confirmedAccount(value, orgId) {
  const cents = (amount) => Number.isSafeInteger(amount) && amount >= 0;
  if (!value || value.orgId !== orgId || value.currency !== "ZAR" || !cents(value.totalCents) || !cents(value.creditCents)) return null;
  if (!["neo", "mc", "bundle"].every((layer) => cents(value.balances?.[layer]))) return null;
  return value.totalCents === value.balances.neo + value.balances.mc + value.balances.bundle ? value : null;
}

function UsageCard({ label, value, detail, total = false }) {
  return <article className={`neo-billing-usage-card${total ? " is-total" : ""}`}>
    <span>{label}</span><strong>{value}</strong><small>{detail}</small>
  </article>;
}

export default function BillingTab() {
  const { user, claims, loading: authLoading } = useAuth();
  const norm = normalizeClaims(claims || {});
  const orgId = String(norm.orgId || "").trim();
  const uid = user?.uid || "";
  const month = currentBillingMonth();
  const scope = `${orgId}|${uid}|${month}`;
  const latestScope = useRef(scope);
  latestScope.current = scope;
  const [usageState, setUsageState] = useState(null);
  const [orgState, setOrgState] = useState(null);
  const [accountState, setAccountState] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const current = !authLoading && uid && usageState?.scope === scope ? usageState : null;
  const preview = current?.preview || null;
  const meta = !authLoading && uid && orgState?.scope === scope ? orgState.meta : null;
  const projectedAccount = confirmedAccount(meta?.billing?.invoiceSummary, orgId);
  const openedAccount = !authLoading && uid && accountState?.scope === scope ? confirmedAccount(accountState.value, orgId) : null;
  const account = openedAccount && (!projectedAccount || openedAccount.revision >= projectedAccount.revision) ? openedAccount : projectedAccount;
  const orgName = preview?.orgName || meta?.name || meta?.orgName || norm.orgName || "Your company";
  const status = account?.access?.neo?.status || meta?.billing?.neoStatus || meta?.neoStatus || "";

  useEffect(() => {
    let active = true;
    if (authLoading || !orgId || !uid) return undefined;
    const stillCurrent = () => active && latestScope.current === scope && auth.currentUser?.uid === uid;
    setUsageState({ scope, preview: null, loading: true, error: "" });
    // One protected, cached calendar preview supplies both layer amounts and site usage.
    getMonthlyBillPreview(orgId, month, refresh > 0).then((data) => {
      if (stillCurrent()) setUsageState({ scope, preview: data, loading: false, error: "" });
    }).catch((error) => {
      if (stillCurrent()) setUsageState({ scope, preview: null, loading: false, error: previewErrorMessage(error) });
    });
    // Reuse the ledger projection; full bills and payments load only when opened.
    getDoc(doc(db, "orgs", orgId)).then((snapshot) => {
      if (stillCurrent()) setOrgState({ scope, meta: snapshot.exists() ? snapshot.data() : null });
    }).catch(() => {
      if (stillCurrent()) setOrgState({ scope, meta: null });
    });
    return () => { active = false; };
  }, [authLoading, orgId, uid, month, scope, refresh]);

  const acceptPreview = useCallback((data) => {
    if (latestScope.current === scope && auth.currentUser?.uid === uid && data.orgId === orgId && data.period?.periodKey === month) {
      setUsageState({ scope, preview: data, loading: false, error: "" });
    }
  }, [orgId, uid, month, scope]);
  const acceptAccount = useCallback((data) => {
    if (latestScope.current === scope && auth.currentUser?.uid === uid && confirmedAccount(data, orgId)) {
      setAccountState({ scope, value: data });
    }
  }, [orgId, uid, scope]);

  const busy = Boolean(authLoading || (orgId && uid && (!current || current.loading)));
  const bundled = preview?.pricingMode === "enterprise_bundle";
  const needsReview = preview && (preview.sourceComplete === false || preview.totalCents == null);
  const layerAmount = (layer) => !preview ? "—" : needsReview ? "Needs review" : bundled ? "Included" : money(preview[layer].amountCents);
  const totalAmount = !preview ? "—" : needsReview ? "Needs review" : money(preview.totalCents);
  const fee = (value) => needsReview ? "Needs review" : bundled ? "Included" : money(value);
  const warnings = preview ? [...new Set([...(preview.warnings || []), ...(preview.startsPending ? ["Some new count starts are waiting to be confirmed. Refresh after the connection recovers."] : [])])] : [];

  return <div className="neo-billing-monthly" aria-busy={busy}>
    <header className="neo-billing-header">
      <div><p className="neo-billing-eyebrow">Neo · Billing & usage</p><h1>Billing</h1><p>Monthly usage, issued bills and confirmed payments.</p></div>
      <div className="neo-billing-actions">
        <MonthlyBillPreview key={`preview-${scope}`} orgId={orgId} orgName={orgName} currentPreview={preview} onPreviewChange={acceptPreview} disabled={busy || !uid || !orgId} />
        {uid && orgId && !authLoading && <BillingAccount key={`account-${scope}`} orgId={orgId} orgName={orgName} label="View bills" onAccountChange={acceptAccount} />}
      </div>
    </header>

    <div className="neo-billing-period"><h2>{monthLabel(month)}</h2><span>Usage estimate · month so far</span></div>
    <section className="neo-billing-usage" aria-label="Current monthly usage">
      <UsageCard label="Neo this month" value={layerAmount("neo")} detail={preview ? `${preview.neo.sites} site${preview.neo.sites === 1 ? "" : "s"} · ${preview.neo.activeStaff} active staff${preview.neo.trial ? " · Trial" : ""}` : "Loading monthly usage"} />
      <UsageCard label="MC this month" value={layerAmount("mc")} detail={preview ? `${preview.mc.billableSites} site${preview.mc.billableSites === 1 ? "" : "s"} started counts${preview.mc.trial ? " · Trial" : ""}` : "Loading monthly usage"} />
      <UsageCard label={bundled ? "Combined plan this month" : "Neo + MC this month"} value={totalAmount} detail={preview ? `${bundled ? "Includes Neo and MC · " : ""}Next bill ${dateLabel(preview.period.dueDate)}` : "Next bill shown with monthly usage"} total />
    </section>
    <p className="neo-billing-caption">Usage continues through the month. Payments and credit are shown separately below.</p>

    <section className="neo-billing-account" aria-label="Account status and balances">
      <div><span>Neo access</span><strong>{statusLabel(status)}</strong></div>
      <div><span>Outstanding bills · Neo + MC</span><strong>{account ? money(account.totalCents) : "View bills"}</strong></div>
      <div><span>Available account credit</span><strong>{account ? money(account.creditCents) : "View bills"}</strong></div>
      <p>{account ? "Credit is available for a future bill; it does not change this month’s usage." : "Open View bills to confirm outstanding balances and available credit."}</p>
    </section>

    <section className="neo-billing-sites">
      <div className="neo-billing-section-title"><div><h2>Neo site usage</h2><p>{preview ? `${preview.neo.clockIns} clock-ins · ${preview.neo.activeStaff} active staff this month` : "Monthly staff and site activity"}</p></div>
        <button type="button" className="matrixos-bill-button" disabled={busy || !uid || !orgId} onClick={() => setRefresh((value) => value + 1)}>Refresh usage</button>
      </div>
      {busy && <p className="neo-billing-state" role="status">Loading this month’s usage…</p>}
      {current?.error && <p className="neo-billing-notice" role="alert">{current.error}</p>}
      {!authLoading && !orgId && <p className="neo-billing-notice" role="alert">Select your company to view billing.</p>}
      {!authLoading && orgId && !uid && <p className="neo-billing-notice" role="alert">Sign in again to view billing.</p>}
      {preview && <>
        {!bundled && <p className="neo-billing-rule">Each site uses the higher of {money(preview.rates.neoMinimumCents)} or active staff × {money(preview.rates.neoStaffCents)}. Sites without active staff have {money(0)} usage.</p>}
        <NeoSiteUsage neo={preview.neo} fee={fee} variant="neo" showTotal />
        {warnings.length > 0 && <details className="neo-billing-notice"><summary>Checks before issuing a bill</summary><ul>{warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul></details>}
      </>}
    </section>
    <footer className="neo-billing-footer">MatrixOS | Operate with Clarity.</footer>
  </div>;
}
